import { apiUrl, API_BASE } from '../utils/apiBase';
import { getImageProxyUrl } from './imageProxyUtil';
import React, { useEffect, useRef, useState } from "react";
import mpegts from "mpegts.js";
import { Play, Cast } from "lucide-react";
import { Server } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";
import { useNotifications } from "../contexts/NotificationContext";
import { createProxyStreamUrl } from "../hooks/useM3UPlaylist";
import BroadcastButton from "./BroadcastButton";
import BroadcastChat from "./BroadcastChat";

function VideoPlayer({ channel, onClose, dark = true }) {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [airPlayAvailable, setAirPlayAvailable] = useState(false);
  const { user } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { addNotification } = useNotifications();
  
  // Stato per conversione server forzata
  const [forceServerConversion, setForceServerConversion] = useState(false);
  
  // Stati per controllo qualità
  const [availableQualities, setAvailableQualities] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState('auto');
  const [sourceInfo, setSourceInfo] = useState(null);
  const [showQualityControls, setShowQualityControls] = useState(false);
  
  // Stati per box informativo
  const [playerMode, setPlayerMode] = useState('');
  const [videoInfo, setVideoInfo] = useState({
    format: '',
    codec: '',
    resolution: '',
    bitrate: '',
    audioCodec: '',
    channels: ''
  });

  // NUOVO: Stati per broadcast
  const [isBroadcastActive, setIsBroadcastActive] = useState(false);
  const [broadcastToken, setBroadcastToken] = useState(null);
  
  // Stato per memorizzare l'URL finale processato per operazioni esterne
  const [processedUrl, setProcessedUrl] = useState('');

  useEffect(() => {
    const el = videoRef.current;
    let isSafariOniOS = false;
    let isIPhone = false;
    if (typeof window !== 'undefined' && window.navigator) {
      const ua = window.navigator.userAgent;
      // Rileva Safari su iOS (iPhone/iPad/iPod)
      isSafariOniOS = /iP(hone|ad|od)/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
      // Rileva specificamente iPhone (per messaggio limitazione AirPlay)
      isIPhone = /iPhone/.test(ua);
      setAirPlayAvailable(true);
    }
    if (
      (el && (typeof el.webkitShowPlaybackTargetPicker === 'function' ||
      (el.remote && typeof el.remote.prompt === 'function')))
      || isSafariOniOS
    ) {
      setAirPlayAvailable(true);
    }
  }, []);

  // Funzione per destroy sicuro del player mpegts.js
  function safeDestroyPlayer(player) {
    if (!player) return;
    
    try {
      // Verifica che il player engine esista prima di procedere
      if (!player._player_engine) {
        console.log('⚠️ Player engine già distrutto, skip cleanup');
        return;
      }
      
      // Pause prima del destroy se possibile
      if (player.pause && typeof player.pause === 'function') {
        try {
          player.pause();
        } catch (pauseError) {
          console.warn('⚠️ Errore durante pause:', pauseError);
        }
      }
      
      // Unload se disponibile
      if (player.unload && typeof player.unload === 'function') {
        try {
          player.unload();
        } catch (unloadError) {
          console.warn('⚠️ Errore durante unload:', unloadError);
        }
      }
      
      // Detach se disponibile  
      if (player.detachMediaElement && typeof player.detachMediaElement === 'function') {
        try {
          player.detachMediaElement();
        } catch (detachError) {
          console.warn('⚠️ Errore durante detach:', detachError);
        }
      }
      
      // Destroy finale solo se il player engine esiste ancora
      if (player.destroy && typeof player.destroy === 'function' && player._player_engine) {
        player.destroy();
        console.log('✅ Player mpegts.js distrutto correttamente');
      }
    } catch (e) {
      console.warn('⚠️ Errore durante destroy sicuro:', e);
    }
  }

  // Funzione per estrarre informazioni video
  function extractVideoInfo(videoEl, mode) {
    setPlayerMode(mode);
    
    if (!videoEl) return;
    
    // Estrazione informazioni di base sempre disponibili
    const info = {
      format: '',
      codec: '',
      resolution: '',
      bitrate: '',
      audioCodec: '',
      channels: ''
    };
    
    try {
      // Risoluzione video
      if (videoEl.videoWidth && videoEl.videoHeight) {
        info.resolution = `${videoEl.videoWidth}x${videoEl.videoHeight}`;
      }
      
      // Informazioni dal source URL
      const url = videoEl.src || videoEl.currentSrc || '';
      if (url.includes('.ts')) info.format = 'MPEG-TS';
      else if (url.includes('.m3u8')) info.format = 'HLS';
      else if (url.includes('.mp4')) info.format = 'MP4';
      else if (url.includes('/proxy/convert')) info.format = 'MP4 (Server Converted)';
      
      // Estrai codec video dal MIME type se disponibile
      if (videoEl.canPlayType) {
        // Test comuni codec video
        if (videoEl.canPlayType('video/mp4; codecs="avc1.42E01E"')) info.codec = 'H.264';
        else if (videoEl.canPlayType('video/mp4; codecs="hev1.1.6.L93.B0"')) info.codec = 'H.265';
        else if (videoEl.canPlayType('video/webm; codecs="vp9"')) info.codec = 'VP9';
        else if (videoEl.canPlayType('video/webm; codecs="vp8"')) info.codec = 'VP8';
      }
      
      // Estrai informazioni audio
      if (videoEl.audioTracks && videoEl.audioTracks.length > 0) {
        const audioTrack = videoEl.audioTracks[0];
        if (audioTrack.label) info.audioCodec = audioTrack.label;
      } else {
        // Test comuni codec audio
        if (videoEl.canPlayType && videoEl.canPlayType('audio/mp4; codecs="mp4a.40.2"')) info.audioCodec = 'AAC';
        else if (videoEl.canPlayType && videoEl.canPlayType('audio/webm; codecs="opus"')) info.audioCodec = 'Opus';
      }
      
      // Calcola bitrate in modo più accurato
      if (videoEl.buffered && videoEl.buffered.length > 0 && videoEl.duration) {
        try {
          const bufferedBytes = videoEl.buffered.end(0) * 1000; // Stima in KB
          const bufferedTime = videoEl.buffered.end(0);
          if (bufferedTime > 0) {
            const estimatedBitrate = Math.round((bufferedBytes * 8) / bufferedTime); // kbps
            info.bitrate = `${estimatedBitrate}k`;
          }
        } catch (e) {}
      }
      
      // Rileva numero canali audio
      try {
        if (window.AudioContext || window.webkitAudioContext) {
          // Stima canali audio (2ch di default per web)
          info.channels = '2ch';
        }
      } catch (e) {}
      
      // Per player mpegts.js, prova a ottenere info aggiuntive
      if (mode === 'mpegts.js' && playerRef.current) {
        try {
          const mediaInfo = playerRef.current.mediaInfo;
          if (mediaInfo) {
            if (mediaInfo.videoCodec) info.codec = mediaInfo.videoCodec;
            if (mediaInfo.audioCodec) info.audioCodec = mediaInfo.audioCodec;
            if (mediaInfo.width && mediaInfo.height) {
              info.resolution = `${mediaInfo.width}x${mediaInfo.height}`;
            }
          }
        } catch (e) {
          console.warn('Errore estrazione mediaInfo mpegts:', e);
        }
      }
      
      // Per player nativo, usa VideoElement APIs più avanzate
      if (mode === 'H5Native') {
        // Estrai informazioni più dettagliate dai metadati
        const loadMetadata = () => {
          try {
            if (videoEl.videoTracks && videoEl.videoTracks.length > 0) {
              const track = videoEl.videoTracks[0];
              if (track.label) info.codec = track.label;
            }
            
            if (videoEl.audioTracks && videoEl.audioTracks.length > 0) {
              const track = videoEl.audioTracks[0];
              if (track.label) info.audioCodec = track.label;
            }
          } catch (e) {
            console.warn('Errore estrazione metadati avanzati:', e);
          }
        };
        
        if (videoEl.readyState >= 1) {
          loadMetadata();
        } else {
          videoEl.addEventListener('loadedmetadata', loadMetadata, { once: true });
        }
      }
      
    } catch (e) {
      console.warn('Errore estrazione info video:', e);
    }
    
    setVideoInfo(info);
  }

  // Funzione per tentare riproduzione con mpegts.js - VERSIONE STABILE
  async function tryMpegtsPlayback(url, videoEl) {
    return new Promise((resolve) => {
      let player = null;
      let resolved = false;
      
      try {
        // Verifica base
        if (!videoEl || !videoEl.parentNode) {
          console.warn('❌ Video element non valido per mpegts.js');
          resolve(false);
          return;
        }

        console.log('🎯 Inizializzazione mpegts.js player per:', url);
        
        // 🔒 TO-BE: Assicurati che mpegts.js usi sempre l'URL proxy completo
        // Non permettere a mpegts.js di estrarre l'URL originale
        const proxyUrl = url.startsWith('/') ? window.location.origin + url : url;
        console.log('🔒 URL proxy completo per mpegts.js:', proxyUrl);
        
        // Configurazione ottimizzata per broadcast stream
        const isBroadcastStream = url.includes('/api/broadcast/stream/') || url.includes('/broadcast/stream/');
        const playerConfig = {
          type: 'mse',
          isLive: true,
          url: proxyUrl, // 🔒 Usa sempre l'URL proxy completo
          cors: true,
          withCredentials: true, // 🔒 Sempre true per proxy VPN
          enableWorker: false,
          enableStashBuffer: true,
          stashInitialSize: isBroadcastStream ? 128 : 384, // Buffer più piccolo per broadcast
          autoCleanupSourceBuffer: true,
          headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; KainoTV/1.0)',
            'Accept': 'video/*, application/*, */*',
            'Referer': window.location.origin, // 🔒 Aggiungi referer per proxy
          }
        };
        
        // Configurazione speciale per broadcast stream
        if (isBroadcastStream) {
          playerConfig.liveBufferLatencyChasing = true;
          playerConfig.liveBufferLatencyMaxLatency = 3;
          playerConfig.liveBufferLatencyMinRemain = 0.5;
        }

        player = mpegts.createPlayer(playerConfig);
        
        // Timeout standard
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            safeDestroyPlayer(player);
            console.log('⏰ Timeout mpegts.js dopo 15 secondi');
            resolve(false);
          }
        }, 15000);
        
        // Event handlers semplici
        const onSuccess = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            playerRef.current = player;
            setTimeout(() => extractVideoInfo(videoEl, 'mpegts.js'), 500);
            resolve(true);
          }
        };
        
        const onError = (errorType, errorDetail) => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            safeDestroyPlayer(player);
            console.warn('❌ mpegts.js error:', errorType, errorDetail);
            resolve(false);
          }
        };

        // Eventi standard
        player.on(mpegts.Events.LOADING_COMPLETE, onSuccess);
        player.on(mpegts.Events.MEDIA_INFO, onSuccess);
        player.on(mpegts.Events.ERROR, onError);
        
        // Attach semplice
        try {
          player.attachMediaElement(videoEl);
          console.log('✅ mpegts.js attach completato');
        } catch (attachError) {
          console.warn('❌ mpegts.js attach error:', attachError);
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            safeDestroyPlayer(player);
            resolve(false);
          }
          return;
        }
        
        // Load e play
        player.load();
        
        setTimeout(() => {
          if (!resolved && player) {
            videoEl.play().catch(() => {});
          }
        }, 100);
        
      } catch (e) {
        console.warn('❌ Errore inizializzazione mpegts.js:', e);
        if (!resolved) {
          resolved = true;
          safeDestroyPlayer(player);
          resolve(false);
        }
      }
    });
  }

  // Funzione per tentare riproduzione nativa
  async function tryNativePlayback(url, videoEl) {
    console.log('🎬 === TENTATIVO PLAYER NATIVO ===');
    console.log('🎬 URL:', url);
    
    return new Promise((resolve) => {
      // Timeout adattivo basato sul tipo di conversione
      const isServerConversion = url.includes('/proxy/convert');
      const timeoutDuration = isServerConversion ? 45000 : 8000; // RIDOTTO: 45s per convert, 8s per normale
      
      // Se è una conversione server-side, mostra progresso
      if (isServerConversion) {
        setStatus('🔄 Conversione server-side in corso (max 45s)...');
        
        // Controlla lo stato del server ogni 10 secondi
        const statusCheckInterval = setInterval(async () => {
          try {
            const response = await fetch(apiUrl('/api/status/conversions'));
            if (response.ok) {
              const status = await response.json();
              if (status.conversions.queueLength > 0) {
                setStatus(`📋 In coda: posizione ${status.conversions.queueLength}`);
              }
            }
          } catch (e) {
            // Ignore status check errors
          }
        }, 10000);
        
        setTimeout(() => clearInterval(statusCheckInterval), timeoutDuration);
      }
      
      const timeout = setTimeout(() => {
        console.log(`⏰ Timeout player nativo dopo ${timeoutDuration/1000} secondi`);
        if (isServerConversion) {
          setStatus('⚠️ Conversione server-side timeout - server sovraccarico');
        }
        resolve(false);
      }, timeoutDuration);
      
      const onCanPlay = () => {
        console.log('✅ Player nativo: evento canplay ricevuto');
        clearTimeout(timeout);
        videoEl.removeEventListener('canplay', onCanPlay);
        videoEl.removeEventListener('error', onError);
        // Estrai informazioni video per player nativo
        setTimeout(() => extractVideoInfo(videoEl, 'H5Native'), 500);
        resolve(true);
      };
      
      const onError = (e) => {
        console.log('❌ Player nativo: errore -', e.target.error?.message || 'Errore sconosciuto');
        clearTimeout(timeout);
        videoEl.removeEventListener('canplay', onCanPlay);
        videoEl.removeEventListener('error', onError);
        
        if (isServerConversion) {
          setStatus('❌ Conversione server-side fallita - prova VLC');
        }
        resolve(false);
      };
      
      videoEl.addEventListener('canplay', onCanPlay, { once: true });
      videoEl.addEventListener('error', onError, { once: true });
      
      try {
        console.log('🎬 Player nativo: impostazione src e avvio...');
        videoEl.src = url;
        videoEl.load();
        videoEl.play().catch((playError) => {
          console.log('⚠️ Player nativo: errore play -', playError.message);
          clearTimeout(timeout);
          resolve(false);
        });
      } catch (setupError) {
        console.log('❌ Player nativo: errore setup -', setupError.message);
        clearTimeout(timeout);
        resolve(false);
      }
    });
  }

  // Utility per rilevare formato e strategia di riproduzione
  function getFileExtension(url) {
    try {
      // Se è un URL proxy, estrai l'URL originale
      let actualUrl = url;
      if (url.includes('/proxy/')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        const originalUrl = urlParams.get('url');
        if (originalUrl) {
          actualUrl = decodeURIComponent(originalUrl);
        }
      }
      
      const urlObj = new URL(actualUrl.includes('://') ? actualUrl : `http://${actualUrl}`);
      const pathname = urlObj.pathname;
      const ext = pathname.split('.').pop().toLowerCase();
      return ext;
    } catch {
      return '';
    }
  }

  function detectPlayerStrategy(url) {
    const ext = getFileExtension(url);
    
    // BROADCAST STREAM: priorità assoluta per mpegts.js
    if (url.includes('/api/broadcast/stream/') || url.includes('/broadcast/stream/')) {
      return 'mpegts';
    }
    
    // Stream live: priorità massima per mpegts.js
    if (url.includes('.ts') || url.includes('m3u8') || url.includes('/live/') || 
        url.includes('/stream') || url.includes('8080') || url.includes('rtmp') ||
        url.includes('/proxy/stream') || !ext || ext.length === 0) {
      return 'mpegts';
    }
    
    // Tutti i formati video: player nativo (con conversione server-side se iOS)
    return 'native';
  }
  
  // Funzione per rilevare il tipo di stream
  function detectStreamType(url) {
    const urlLower = url.toLowerCase();
    
    // Prima estrai l'estensione dal file
    const ext = getFileExtension(url);
    console.log('🔍 URL:', urlLower);
    console.log('🔍 Estensione rilevata:', ext);
    
    // BROADCAST STREAM: priorità assoluta
    if (urlLower.includes('/api/broadcast/stream/') || urlLower.includes('/broadcast/stream/')) {
      return 'broadcast_stream';
    }
    
    // IPTV patterns
    if (urlLower.includes('iptv') || urlLower.includes('/live/') || 
        urlLower.includes(':8080') || urlLower.includes(':1935')) {
      return 'iptv';
    }
    
    // HLS streams
    if (urlLower.includes('.m3u8') || urlLower.includes('/playlist.m3u8')) {
      return 'hls';
    }
    
    // DASH streams
    if (urlLower.includes('.mpd') || urlLower.includes('dash')) {
      return 'dash';
    }
    
    // RTMP/RTSP
    if (urlLower.startsWith('rtmp://') || urlLower.startsWith('rtmps://') ||
        urlLower.startsWith('rtsp://') || urlLower.startsWith('rtsps://')) {
      return 'rtmp_rtsp';
    }
    
    // UDP/RTP
    if (urlLower.startsWith('udp://') || urlLower.startsWith('rtp://')) {
      return 'udp_rtp';
    }
    
    // HTTP progressive streams
    if (urlLower.includes('/stream') || urlLower.includes('.ts')) {
      return 'http_progressive';
    }
    
    // YouTube/Vimeo style
    if (urlLower.includes('youtube') || urlLower.includes('vimeo') ||
        urlLower.includes('dailymotion') || urlLower.includes('twitch')) {
      return 'video_platform';
    }
    
    // File video standard
    if (['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', '3gp', 'mts', 'f4v', 'divx', 'asf'].includes(ext)) {
      return 'video_file';
    }
    
    // Se contiene "movie" o pattern tipici di film, consideralo file video
    if (urlLower.includes('/movie/') || urlLower.includes('film') || 
        urlLower.includes('cinema') || urlLower.includes('video')) {
      return 'video_file';
    }
    
    return 'unknown';
  }

  // Funzione principale di riproduzione - STRATEGIA SEMPLIFICATA
  async function attemptVideoPlayback(url, videoEl) {
    const strategy = detectPlayerStrategy(url);
    const mpegtsAvailable = mpegts && mpegts.getFeatureList && mpegts.getFeatureList().mseLivePlayback;
    const ext = getFileExtension(url);
    
    console.log('🎯 === INIZIO RIPRODUZIONE VIDEO ===');
    console.log('🎮 Strategia scelta:', strategy);
    console.log('🔧 mpegts.js disponibile:', mpegtsAvailable);
    console.log('🌐 Video element:', videoEl ? 'OK' : 'ERRORE');
    
    // Scegli il proxy giusto in base al tipo di contenuto e dispositivo
    let finalUrl = url;
    const streamType = detectStreamType(url);
    const isiOS = /iP(hone|ad|od)/.test(navigator.userAgent);
    
    // BROADCAST STREAM: non modificare mai l'URL
    if (streamType === 'broadcast_stream') {
      finalUrl = url;
      console.log('📡 [BROADCAST-PLAYER] Stream detected:', {
        originalUrl: url,
        finalUrl,
        protocol: window.location.protocol,
        host: window.location.host,
        origin: window.location.origin,
        timestamp: new Date().toISOString()
      });
    }
    // Per file video su iOS o conversione forzata, usa conversione server-side
    else {
      const isVideoFile = streamType === 'video_file' || 
                         ['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', '3gp', 'mts', 'f4v', 'divx', 'asf'].includes(ext);
      
      // Se la conversione è forzata O siamo su iOS con file video, forza /proxy/convert
      if ((isVideoFile && isiOS) || forceServerConversion) {
        // Estrai l'URL originale se è già un proxy
        let originalUrl = url;
        if (url.includes('/proxy/')) {
          const urlParams = new URLSearchParams(url.split('?')[1]);
          const extractedUrl = urlParams.get('url');
          if (extractedUrl) {
            originalUrl = decodeURIComponent(extractedUrl);
          }
        }
        
        // Aggiungi parametro di qualità se specificato
        const qualityParam = selectedQuality !== 'auto' ? `&quality=${selectedQuality}` : '';
        finalUrl = `/proxy/convert?url=${encodeURIComponent(originalUrl)}${qualityParam}`;
        
        if (forceServerConversion) {
          console.log('🔧 Conversione server forzata dall\'utente:', url, '→', finalUrl, 'Qualità:', selectedQuality);
        } else {
          console.log('🍎 iOS + File video: usando proxy/convert:', url, '→', finalUrl, 'Qualità:', selectedQuality);
        }
      } else if (!url.includes('/proxy/') && !url.startsWith('/broadcast/') && !url.startsWith('/api/broadcast/stream/')) {
        // Per stream TV e altri contenuti, usa proxy/image solo se non è già proxy
        // ma evita di modificare gli URL locali del broadcast
        finalUrl = getImageProxyUrl(url);
        console.log('🔄 Convertendo a proxy/image:', url, '→', finalUrl);
      } else {
        console.log('✅ URL già proxy (mantenuto):', url);
      }
    }
    
    // Salva l'URL finale per operazioni esterne (copia e VLC) - usa lo stesso URL del video
    setProcessedUrl(finalUrl);
    
    setStatus(`🎯 Strategia: ${strategy.toUpperCase()} - Riproduzione semplificata`);
    
    // STRATEGIA SEMPLIFICATA: Solo 4 modalità
    
    // 1. BROADCAST STREAM → Solo mpegts.js (no fallback)
    if (streamType === 'broadcast_stream') {
      console.log('📡 [BROADCAST-PLAYER] Starting broadcast playback:', {
        finalUrl,
        mpegtsAvailable,
        timestamp: new Date().toISOString()
      });

      if (!mpegtsAvailable) {
        console.error('❌ [BROADCAST-PLAYER] mpegts.js not available');
        setStatus('❌ Broadcast richiede mpegts.js non disponibile');
        return false;
      }
      
      setStatus('📡 Broadcast stream: mpegts.js...');
      console.log('📡 [BROADCAST-PLAYER] Attempting mpegts.js playback:', finalUrl);
      
      const success = await tryMpegtsPlayback(finalUrl, videoEl);
      
      console.log('📡 [BROADCAST-PLAYER] mpegts.js result:', {
        success,
        finalUrl,
        timestamp: new Date().toISOString()
      });

      if (success) return true;
      
      console.error('❌ [BROADCAST-PLAYER] mpegts.js failed:', finalUrl);
      setStatus('❌ Errore broadcast stream - verifica connessione');
      return false;
    }
    
    // 2. iOS + File Video o Conversione Forzata → Solo server-side conversion
    if (((streamType === 'video_file' && isiOS) || forceServerConversion) && finalUrl.includes('/proxy/convert')) {
      setStatus(forceServerConversion ? 
        '🔧 Conversione server forzata...' : 
        '🍎 iOS + File video: conversione server-side...');
      const success = await tryNativePlayback(finalUrl, videoEl);
      if (success) return true;
      
      console.log(forceServerConversion ? 
        '❌ Conversione server forzata fallita' : 
        '❌ iOS + File video: conversione server-side fallita');
      return false;
    }
    
    // 3. Stream Live → mpegts.js
    if (strategy === 'mpegts' && mpegtsAvailable) {
      setStatus('📡 Stream live: mpegts.js...');
      const success = await tryMpegtsPlayback(finalUrl, videoEl);
      if (success) return true;
      
      // Fallback al player nativo
      setStatus('🔄 Fallback: player nativo...');
      const nativeSuccess = await tryNativePlayback(finalUrl, videoEl);
      if (nativeSuccess) return true;
    }
    
    // 4. Tutto il resto → Player nativo
    setStatus('🎬 Player nativo universale...');
    const nativeSuccess = await tryNativePlayback(finalUrl, videoEl);
    if (nativeSuccess) return true;
    
    // Ultimo tentativo con mpegts.js se non già provato
    if (strategy !== 'mpegts' && mpegtsAvailable) {
      setStatus('🔄 Ultimo tentativo: mpegts.js...');
      const mpegtsSuccess = await tryMpegtsPlayback(finalUrl, videoEl);
      if (mpegtsSuccess) return true;
    }
    
    return false;
  }

  // Funzione per analizzare le qualità video disponibili
  async function analyzeVideoQuality(url) {
    if (!forceServerConversion) return;
    
    try {
      setStatus('🔍 Analizzando qualità video...');
      
      // Estrai URL originale se necessario
      let originalUrl = url;
      if (url.includes('/proxy/')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        const extractedUrl = urlParams.get('url');
        if (extractedUrl) {
          originalUrl = decodeURIComponent(extractedUrl);
        }
      }
      
      const response = await fetch(apiUrl(`/proxy/convert?url=${encodeURIComponent(originalUrl)}&quality=analyze`));
      if (response.ok) {
        const analysis = await response.json();
        setSourceInfo(analysis);
        setAvailableQualities(analysis.availableQualities || []);
        
        // Seleziona automaticamente la qualità migliore disponibile (default 1080p se disponibile, max 1080p)
        const defaultQuality = analysis.availableQualities.includes('1080p') ? '1080p' : 
                              analysis.availableQualities.includes('720p') ? '720p' : 
                              analysis.availableQualities[0] || 'auto';
        setSelectedQuality(defaultQuality);
        
        console.log('📊 Analisi video:', analysis);
        console.log('🎯 Qualità disponibili (max 1080p):', analysis.availableQualities);
        console.log('🎯 Qualità selezionata automaticamente:', defaultQuality);
        
        const resolutionInfo = analysis.sourceResolution.includes('x') ? 
          analysis.sourceResolution.split('x').map(Number) : [0, 0];
        const sourceHeight = resolutionInfo[1] || 0;
        
        setStatus(sourceHeight > 1080 ? 
          `📊 Rilevato: ${analysis.sourceResolution} → max 1080p (${analysis.availableQualities.length} opzioni)` :
          `📊 Rilevato: ${analysis.sourceResolution} - ${analysis.availableQualities.length} opzioni qualità`);
        setTimeout(() => setStatus(''), 3000);
      } else {
        console.warn('⚠️ Analisi qualità fallita');
        setAvailableQualities(['720p']); // Fallback
        setSelectedQuality('720p');
      }
    } catch (error) {
      console.warn('⚠️ Errore analisi qualità:', error);
      setAvailableQualities(['720p']); // Fallback
      setSelectedQuality('720p');
    }
  }

  useEffect(() => {
    if (!channel || !channel.url) return;
    
    const videoEl = videoRef.current;
    if (!videoEl) return;
    
    let cleanup = false;
    
    // Funzione di pulizia completa
    const performCleanup = () => {
      // Reset informazioni video
      setPlayerMode('');
      setVideoInfo({
        format: '',
        codec: '',
        resolution: '',
        bitrate: '',
        audioCodec: '',
        channels: ''
      });
      
      // Reset URL processato
      setProcessedUrl('');
      
      // Distruggi player precedente
      if (playerRef.current) {
        safeDestroyPlayer(playerRef.current);
        playerRef.current = null;
      }
      
      // Reset completo dell'elemento video
      if (videoEl) {
        try {
          // Ferma la riproduzione
          videoEl.pause();
          
          // Cleanup blob URL se presente
          if (videoEl.currentBlobURL) {
            URL.revokeObjectURL(videoEl.currentBlobURL);
            videoEl.currentBlobURL = null;
          }
          
          // Reset src in modo sicuro
          videoEl.src = '';
          videoEl.removeAttribute('src');
          videoEl.load();
          
          // Rimuovi event listeners senza clonare
          const events = ['loadstart', 'loadeddata', 'loadedmetadata', 'canplay', 'canplaythrough', 'error', 'ended', 'timeupdate'];
          events.forEach(event => {
            videoEl.removeEventListener(event, () => {});
          });
          
        } catch (cleanupError) {
          console.warn('⚠️ Errore durante cleanup video element:', cleanupError);
        }
      }
    };
    
    // Esegui pulizia
    performCleanup();
    
    // Aspetta un po' prima di inizializzare il nuovo player
    const initTimeout = setTimeout(async () => {
      if (cleanup) return;
      
      const currentVideoEl = videoRef.current;
      if (!currentVideoEl) return;
      
      // Inizia il caricamento
      setLoading(true);
      const isProxyUrl = channel.url.includes('/proxy/stream?url=');
      const fileExt = getFileExtension(channel.url);
      const strategy = detectPlayerStrategy(channel.url);
      
      let statusMessage = isProxyUrl ? '📡 Stream via proxy server...' : '🎯 Stream diretto...';
      if (fileExt) statusMessage += ` [${fileExt.toUpperCase()}]`;
      if (strategy === 'mpegts') statusMessage += ' 📡 Stream live';
      
      setStatus(statusMessage);
      
      // Tenta la riproduzione
      try {
        const success = await attemptVideoPlayback(channel.url, currentVideoEl);
        
        if (cleanup) return; // Se nel frattempo è stato fatto cleanup, esci
        
        setLoading(false);
        if (success) {
          const strategy = detectPlayerStrategy(channel.url);
          let successMessage = isProxyUrl ? '✅ Stream proxy caricato!' : '✅ Stream caricato!';
          if (strategy === 'mpegts') successMessage = '✅ Stream live caricato!';
          
          setStatus(successMessage);
          setTimeout(() => {
            if (!cleanup) setStatus('');
          }, 3000);
        } else {
          setStatus('❌ Errore riproduzione - Prova ad aprire in VLC');
          setTimeout(() => {
            if (!cleanup) setStatus('');
          }, 5000);
        }
      } catch (error) {
        console.error('Errore durante il tentativo di riproduzione:', error);
        if (!cleanup) {
          setLoading(false);
          setStatus('❌ Errore riproduzione - Prova ad aprire in VLC');
        }
      }
    }, 50);
    
    return () => {
      cleanup = true;
      clearTimeout(initTimeout);
      
      if (playerRef.current) {
        safeDestroyPlayer(playerRef.current);
        playerRef.current = null;
      }
      
      // Cleanup blob URL se presente
      const videoEl = videoRef.current;
      if (videoEl && videoEl.currentBlobURL) {
        URL.revokeObjectURL(videoEl.currentBlobURL);
        videoEl.currentBlobURL = null;
      }
    };
  }, [channel, forceServerConversion, selectedQuality]); // Aggiungiamo selectedQuality alle dipendenze

  // Funzione per chiudere il player
  function closePlayer() {
    if (playerRef.current) {
      safeDestroyPlayer(playerRef.current);
      playerRef.current = null;
    }
    
    const videoEl = videoRef.current;
    if (videoEl) {
      // Cleanup blob URL se presente
      if (videoEl.currentBlobURL) {
        URL.revokeObjectURL(videoEl.currentBlobURL);
        videoEl.currentBlobURL = null;
      }
      videoEl.src = '';
      videoEl.load();
    }
    
    if (onClose) onClose();
  }

  // Funzione per toggle conversione server
  function toggleServerConversion() {
    const newValue = !forceServerConversion;
    setForceServerConversion(newValue);
    
    // Reset stati qualità quando si disattiva
    if (!newValue) {
      setAvailableQualities([]);
      setSelectedQuality('auto');
      setSourceInfo(null);
    }
    
    // Mostra feedback immediato
    setStatus(newValue ? 
      '🔧 Attivando conversione server...' : 
      '📱 Disattivando conversione server...');
    setLoading(true);
    
    // Se si attiva la conversione server, analizza automaticamente la qualità
    if (newValue && channel?.url) {
      setTimeout(() => {
        analyzeVideoQuality(channel.url);
      }, 1000);
    }
    
    // Riavvia il player con la nuova impostazione
    // Il nuovo valore sarà utilizzato nel prossimo useEffect
    console.log(`🔧 Toggle conversione server: ${newValue ? 'ATTIVA' : 'DISATTIVA'}`);
  }

  // Ferma il broadcast come admin
  async function stopBroadcast() {
    if (!broadcastToken) return;
    try {
      await authenticatedFetch(`/api/broadcast/stop/${broadcastToken}`, { method: 'POST' });
      setIsBroadcastActive(false);
      setBroadcastToken(null);
      //addNotification({ message: 'Broadcast terminato' });

      // Reload original channel via proxy
      if (channel?.originalUrl) {
        const proxyUrl = await createProxyStreamUrl(channel.originalUrl);
        const updated = {
          ...channel,
          url: proxyUrl,
          name: channel.name.replace(/^\uD83D\uDD34\s*LIVE:\s*/u, '')
        };
        const event = new CustomEvent('updateChannel', { detail: updated });
        window.dispatchEvent(event);
      }
    } catch (e) {
      console.warn('Errore stop broadcast', e);
    }
  }

  // NUOVO: Effetto per verificare se questo canale è già in broadcast
  useEffect(() => {
    const checkBroadcastStatus = async () => {
      if (!channel?.url || !user) return;
      
      // Se è già un URL di broadcast, estrai le info
      if (channel.url.includes('/api/broadcast/stream/')) {
        const tokenPart = channel.url.split('/').pop();
        const token = tokenPart.split('?')[0];
        setIsBroadcastActive(true);
        setBroadcastToken(token);
      } else {
        setIsBroadcastActive(false);
        setBroadcastToken(null);
      }
    };

    checkBroadcastStatus();
  }, [channel, user]);

  if (!channel) return null;
  
  const borderColor = isBroadcastActive
    ? (user?.role === 'admin' ? 'border-red-500' : 'border-blue-500')
    : (dark ? 'border-white/10' : 'border-black/10');

  return (
    <div className={`mb-6 backdrop-blur-sm border rounded-xl p-6 ${dark ? 'bg-white/5' : 'bg-black/5'} ${borderColor}`}>
      {/* Status bar */}
      {(status || loading) && (
        <div className={`mb-4 p-3 backdrop-blur-sm rounded-lg border flex items-center gap-2 ${
          dark 
            ? 'bg-white/10 border-white/20' 
            : 'bg-black/10 border-black/20'
        }`}>
          {loading && (
            <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
          )}
          <span className={`text-sm ${dark ? 'text-white/80' : 'text-gray-700'}`}>{status}</span>
        </div>
      )}
      
      {/* Header player */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {channel.tvgLogo && (
            <img 
              src={getImageProxyUrl(channel.tvgLogo)} 
              alt="Logo" 
              className="w-8 h-8 object-contain rounded-lg" 
              onError={(e) => {
                // Se fallisce il proxy interno, proviamo a ritentare una volta
                if (e.target.src.includes('/proxy/image') && channel.originalLogoUrl && !e.target.hasRetried) {
                  e.target.hasRetried = true;
                  // Forza il ricaricamento del nostro proxy aggiungendo un timestamp
                  const separator = e.target.src.includes('?') ? '&' : '?';
                  e.target.src = e.target.src + separator + 'retry=' + Date.now();
                  console.log('🔄 VideoPlayer logo retry:', e.target.src);
                } else {
                  // Se fallisce dopo il retry, nascondi l'immagine
                  e.target.style.display = 'none';
                  console.log('❌ VideoPlayer logo failed definitively');
                }
              }}
            />
          )}
          <h2 className={`text-base font-bold bg-gradient-to-r bg-clip-text text-transparent ${
            dark 
              ? 'from-purple-300 to-pink-300' 
              : 'from-purple-600 to-pink-600'
          }`}>{channel.name}
            {isBroadcastActive && user?.role !== 'admin' && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-600 text-white">LIVE BROADCAST</span>
            )}
          </h2>
          <span className={`text-xs backdrop-blur-sm px-2 py-0.5 rounded-full border ${
            dark 
              ? 'text-white/60 bg-white/10 border-white/20' 
              : 'text-gray-600 bg-black/10 border-black/20'
          }`}>{channel.group}</span>
        </div>
        <button 
          onClick={closePlayer}
          className="w-10 h-10 bg-red-500/20 hover:bg-red-500/40 backdrop-blur-sm border-2 border-red-400 hover:border-red-300 rounded-lg text-red-300 hover:text-red-200 text-lg font-bold transition-all duration-200 flex items-center justify-center hover:scale-110"
          title="Chiudi player"
        >
          ✕
        </button>
      </div>
      {/* Video element con chat */}
      <div className="relative mb-4">
        <video
          ref={videoRef}
          controls
          className={`w-full rounded-xl shadow-2xl border ${borderColor}`}
          style={{ background: "linear-gradient(135deg, #0f0f23, #1a1a2e)", aspectRatio: "16/9" }}
        />
        {broadcastToken && (
          <BroadcastChat token={broadcastToken} />
        )}
      </div>
      
      {/* Controlli player */}
      <div className="flex gap-3 flex-wrap">
        <button
          className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-lg text-white/80 hover:text-white text-sm font-medium transition-all duration-200"
          onClick={() => {
            // Usa lo stesso URL che usa il video player (processedUrl contiene finalUrl)
            const fullUrl = processedUrl.startsWith('/') ? window.location.origin + processedUrl : processedUrl;
            navigator.clipboard?.writeText(fullUrl);
            console.log('🔗 URL copiato:', fullUrl);
          }}
          title="Copia URL"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="7" y="7" width="13" height="13" rx="2" fill="currentColor" fill-opacity="0.2" stroke="currentColor" stroke-width="1.5"/><rect x="4" y="4" width="13" height="13" rx="2" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>

        {user?.role === 'admin' && (
          <BroadcastButton
            channel={channel}
            isBroadcastActive={isBroadcastActive}
            broadcastToken={broadcastToken}
            onBroadcastStarted={(token, link) => {
              setIsBroadcastActive(true);
              setBroadcastToken(token);

              // Aggiorna URL e nome per l'admin con un piccolo delay per permettere l'inizializzazione del server
              setTimeout(() => {
                const updatedChannel = {
                  ...channel,
                  url: link,
                  name: `🔴 LIVE: ${channel.name}`
                };
                const event = new CustomEvent('updateChannel', { detail: updatedChannel });
                window.dispatchEvent(event);
              }, 500); // 500ms di delay per permettere l'inizializzazione dell'upstream
            }}
            onStopBroadcast={stopBroadcast}
            className="px-4 py-2 bg-green-600/20 hover:bg-green-600/40 backdrop-blur-sm border border-green-400/50 rounded-lg text-green-200 text-sm font-medium transition-all duration-200"
          />
        )}
        
        {/* Pulsante conversione server */}
        <button
          className={`px-4 py-2 backdrop-blur-sm border rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            forceServerConversion 
              ? 'bg-blue-500/20 border-blue-400/50 text-blue-300 hover:bg-blue-500/30' 
              : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/20 hover:text-white'
          }`}
          onClick={toggleServerConversion}
          title={forceServerConversion ? 
            "Conversione server attiva - clicca per usare player nativo" : 
            "Forza conversione server-side (raccomandato per file video problematici)"}
        >
          <Server size={18} />
        </button>
        
        {/* Controlli qualità - visibili solo se conversione server è attiva */}
        {forceServerConversion && availableQualities.length > 1 && (
          <div className="flex items-center gap-2">
            <select
              value={selectedQuality}
              onChange={(e) => setSelectedQuality(e.target.value)}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white/80 text-sm font-medium backdrop-blur-sm focus:outline-none focus:border-blue-400/50"
              title="Seleziona qualità di conversione"
            >
              <option value="auto">Auto</option>
              {availableQualities.map(quality => (
                <option key={quality} value={quality} className="bg-gray-800">
                  {quality === 'original' ? 'Originale' : quality.toUpperCase()}
                </option>
              ))}
            </select>
            
            {sourceInfo && (
              <div className="text-xs text-white/60 hidden md:block">
                {sourceInfo.sourceResolution}
              </div>
            )}
          </div>
        )}
        
        {/* Pulsante per analizzare qualità */}
        {forceServerConversion && availableQualities.length === 0 && (
          <button
            className="px-4 py-2 bg-purple-500/20 border border-purple-400/50 rounded-lg text-purple-300 text-sm font-medium backdrop-blur-sm hover:bg-purple-500/30 transition-all duration-200"
            onClick={() => analyzeVideoQuality(channel.url)}
            title="Analizza qualità disponibili"
          >
            🔍 Analizza
          </button>
        )}
        
        {airPlayAvailable && (
          <button
            className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-blue-500/50 hover:border-blue-400/70 rounded-lg text-white/80 hover:text-white text-sm font-medium transition-all duration-200"
            onClick={() => {
              const el = videoRef.current;
              if (!el) return;
              if (typeof el.webkitShowPlaybackTargetPicker === 'function') {
                try { el.webkitShowPlaybackTargetPicker(); } catch (e) { console.warn('AirPlay picker failed', e); }
              } else if (el.remote && typeof el.remote.prompt === 'function') {
                el.remote.prompt().catch((e) => console.warn('Remote prompt failed', e));
              }
            }}
            title="Trasmetti con AirPlay"
          >
            <Cast className="w-5 h-5" />
          </button>
        )}
        <button
          className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-orange-500/50 hover:border-orange-400/70 rounded-lg text-white/80 hover:text-white text-sm font-medium transition-all duration-200"
          onClick={() => {
            // Usa lo stesso URL che usa il video player (processedUrl contiene finalUrl)
            const fullUrl = processedUrl.startsWith('/') ? window.location.origin + processedUrl : processedUrl;
            
            // Rileva se siamo su mobile
            const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            
            if (isMobile) {
              // Mobile: usa callback URL
              const callbackUrl = `vlc-x-callback://x-callback-url/play?url=${encodeURIComponent(fullUrl)}`;
              window.location.href = callbackUrl;
              // Fallback per mobile senza callback
              setTimeout(() => {
                window.open(`vlc://${fullUrl}`, '_blank');
              }, 1000);
            } else {
              // Desktop: prova a lanciare VLC direttamente con l'URL HTTP
              // Su Windows: vlc://URL, su macOS/Linux: l'URL diretto
              const userAgent = navigator.userAgent.toLowerCase();
              let vlcUrl;
              
              if (userAgent.includes('windows')) {
                // Windows: usa protocollo vlc:// ma con URL pulito
                vlcUrl = `vlc://${fullUrl.replace('https://', '').replace('http://', '')}`;
              } else {
                // macOS/Linux: prova protocollo vlc diretto
                vlcUrl = `vlc://${fullUrl}`;
              }
              
              // Prova a lanciare VLC
              const tempLink = document.createElement('a');
              tempLink.href = vlcUrl;
              tempLink.click();
              
              // Mostra notifica con istruzioni per desktop dopo un breve delay
              setTimeout(() => {
                if (confirm('VLC non sembra installato o configurato.\n\nVuoi copiare l\'URL per incollarlo manualmente in VLC?\n(File > Apri URL di rete)')) {
                  navigator.clipboard?.writeText(fullUrl);
                  addNotification({ 
                    message: 'URL copiato! Incollalo in VLC: File > Apri URL di rete',
                    type: 'info'
                  });
                }
              }, 500);
            }
          }}
          title="Apri in VLC Media Player"
        >
          <Play className="w-5 h-5" />
        </button>
        
        {/* Box informativo modalità di riproduzione */}
        {playerMode && (
          <div className={`flex-1 min-w-0 px-3 py-2 backdrop-blur-sm border rounded-lg text-xs ${
            dark 
              ? 'bg-white/5 border-white/10 text-white/70' 
              : 'bg-black/5 border-black/10 text-gray-600'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2 h-2 rounded-full ${
                playerMode === 'mpegts.js' ? 'bg-green-400' :
                playerMode === 'H5Native' ? 'bg-blue-400' : 'bg-gray-400'
              }`}></div>
              <span className="font-medium">{playerMode}</span>
              {forceServerConversion && (
                <span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded text-[10px] font-medium">
                  SERVER
                </span>
              )}
            </div>
            <div className="grid grid-cols-6 gap-x-px gap-y-px text-[8px] leading-none">
              {videoInfo.format && (
                <div><span className="opacity-60">Format:</span> {videoInfo.format}</div>
              )}
              {videoInfo.resolution && (
                <div><span className="opacity-60">Resolution:</span> {videoInfo.resolution}</div>
              )}
              {videoInfo.codec && (
                <div><span className="opacity-60">Video:</span> {videoInfo.codec}</div>
              )}
              {videoInfo.bitrate && (
                <div><span className="opacity-60">Bitrate:</span> {videoInfo.bitrate}</div>
              )}
              {videoInfo.audioCodec && (
                <div><span className="opacity-60">Audio:</span> {videoInfo.audioCodec}</div>
              )}
              {videoInfo.channels && (
                <div><span className="opacity-60">Channels:</span> {videoInfo.channels}</div>
              )}
            </div>
          </div>
        )}
      </div>
      
    </div>
  );
}

export default VideoPlayer;
